// ===========================
// DATA MODELS
// ===========================

import { generateId } from './utils.js';
import { dimensionData } from './dimensions.js';
import { sqlAttributeData } from './sql-options.js';

export class Attribute {
    constructor(id, name, type = 'VARCHAR', isPK = false, isUQ = false, isNull = true, defaultValue = '') {
        this.id = id || generateId('attr');
        this.name = name || 'nouvel_attribut';
        this.type = type;
        this.isPK = isPK;
        this.isUQ = isUQ;
        this.isNull = isNull;
        this.defaultValue = defaultValue;
        this.enumValues = [];
    }

    clone() {
        const attr = new Attribute(
            generateId('attr'),
            this.name,
            this.type,
            this.isPK,
            this.isUQ,
            this.isNull,
            this.defaultValue
        );
        attr.enumValues = [...this.enumValues];
        Object.assign(attr, sqlAttributeData(this));
        return attr;
    }

    toJSON() {
        return {
            id: this.id,
            name: this.name,
            type: this.type,
            isPK: this.isPK,
            isUQ: this.isUQ,
            isNull: this.isNull,
            defaultValue: this.defaultValue,
            ...sqlAttributeData(this),
            enumValues: [...this.enumValues]
        };
    }

    static fromJSON(json) {
        const attr = new Attribute(
            json.id,
            json.name,
            json.type,
            Boolean(json.isPK),
            Boolean(json.isUQ),
            Boolean(json.isNull),
            json.defaultValue || ''
        );
        attr.enumValues = Array.isArray(json.enumValues) ? [...json.enumValues] : [];
        Object.assign(attr, sqlAttributeData(json));
        return attr;
    }
}

export class Entity {
    constructor(id, name, x, y, attributes = []) {
        this.id = id || generateId('entity');
        this.name = name || 'Nouvelle Entité';
        this.x = x ?? 100;
        this.y = y ?? 100;
        this.type = 'entity';
        this.attributes = attributes.length ? attributes : [
            new Attribute(generateId('attr'), 'id', 'INTEGER', true, false, false, '')
        ];
    }

    clone() {
        const copy = new Entity(
            generateId('entity'),
            this.name + ' (copie)',
            this.x + 50,
            this.y + 50,
            this.attributes.map(attr => attr.clone())
        );
        return Object.assign(copy, dimensionData(this));
    }

    toJSON() {
        return {
            id: this.id,
            name: this.name,
            x: this.x,
            y: this.y,
            type: this.type,
            ...dimensionData(this),
            attributes: this.attributes.map(a => a.toJSON())
        };
    }

    static fromJSON(json) {
        const entity = new Entity(json.id, json.name, json.x, json.y);
        entity.attributes = (json.attributes || []).map(Attribute.fromJSON);
        Object.assign(entity, dimensionData(json));
        return entity;
    }
}

export class Association {
    constructor(id, name, x, y, attributes = []) {
        this.id = id || generateId('assoc');
        this.name = name || 'Association';
        this.x = x ?? 100;
        this.y = y ?? 100;
        this.type = 'association';
        this.attributes = attributes;
    }

    clone() {
        const copy = new Association(
            generateId('assoc'),
            this.name + ' (copie)',
            this.x + 50,
            this.y + 50,
            this.attributes.map(attr => attr.clone())
        );
        return Object.assign(copy, dimensionData(this));
    }

    toJSON() {
        return {
            id: this.id,
            name: this.name,
            x: this.x,
            y: this.y,
            type: this.type,
            ...dimensionData(this),
            attributes: this.attributes.map(a => a.toJSON())
        };
    }

    static fromJSON(json) {
        const assoc = new Association(json.id, json.name, json.x, json.y);
        assoc.attributes = (json.attributes || []).map(Attribute.fromJSON);
        Object.assign(assoc, dimensionData(json));
        return assoc;
    }
}

export class Connection {
    constructor(id, associationId, entityId, cardinality = '1,n', label = '') {
        this.id = id || generateId('conn');
        this.associationId = associationId;
        this.entityId = entityId;
        this.cardinality = cardinality;
        this.label = label;
    }

    toJSON() {
        return {
            id: this.id,
            associationId: this.associationId,
            entityId: this.entityId,
            cardinality: this.cardinality,
            label: this.label
        };
    }

    static fromJSON(json) {
        return new Connection(
            json.id,
            json.associationId,
            json.entityId,
            json.cardinality,
            json.label || ''
        );
    }
}

export const MAX_TEXT_NOTES = 500;

export class TextAnnotation {
    constructor(id, text = '', x = 0, y = 0) {
        this.id = id || generateId('text');
        this.type = 'text';
        this.text = text; this.x = x; this.y = y;
        this.fontSize = 18; this.bold = false; this.italic = false; this.underline = false;
    }
    toJSON() {
        return { id: this.id, text: this.text, x: this.x, y: this.y, fontSize: this.fontSize,
            ...dimensionData(this),
            bold: this.bold, italic: this.italic, underline: this.underline };
    }
    static fromJSON(data) {
        return Object.assign(new TextAnnotation(data.id, data.text, data.x, data.y), {
            ...dimensionData(data),
            fontSize: data.fontSize, bold: data.bold, italic: data.italic, underline: data.underline
        });
    }
    clone() {
        return TextAnnotation.fromJSON({ ...this.toJSON(), id: generateId('text'), x: this.x + 30, y: this.y + 30 });
    }
}
